import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const priorityColors = {
  low: 'bg-green-500/20 text-green-400 border-green-500/50',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  urgent: 'bg-red-500/20 text-red-400 border-red-500/50',
  very_urgent: 'bg-pink-500/20 text-pink-400 border-pink-500/50'
};

const priorityLabels = {
  low: 'Düşük',
  medium: 'Orta',
  high: 'Yüksek',
  urgent: 'Acil',
  very_urgent: 'Çok Acil'
};

const departmentLabels = {
  'bakim_onarim': 'Bakım-Onarım',
  'emlak_kamulastirma': 'Emlak ve Kamulaştırma',
  'yatirim_insaat': 'Yatırım ve İnşaat'
};

const PendingTasksPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pendingTasks, setPendingTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchPendingTasks();
    fetchUsers();
  }, []);

  const fetchPendingTasks = async () => {
    try {
      const response = await axios.get(`${API}/pending-tasks`, {
        withCredentials: true
      });
      setPendingTasks(response.data);
    } catch (error) {
      console.error('Pending tasks fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  };

  const handleApprove = async (taskId) => {
    if (!window.confirm('Bu görevi onaylamak istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await axios.post(`${API}/pending-tasks/${taskId}/approve`, {}, {
        withCredentials: true
      });
      alert('✅ Görev onaylandı ve sisteme eklendi!');
      fetchPendingTasks();
    } catch (error) {
      console.error('Approve error:', error);
      alert('❌ Hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleReject = async (taskId) => {
    if (!window.confirm('Bu görev önerisini reddetmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await axios.delete(`${API}/pending-tasks/${taskId}`, {
        withCredentials: true
      });
      alert('✅ Görev önerisi reddedildi!');
      fetchPendingTasks();
    } catch (error) {
      console.error('Reject error:', error);
      alert('❌ Hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Yatırım ve İnşaat</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                data-testid="anasayfa-nav-button"
              >
                Anasayfa
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Dashboard
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/tasks')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Görevler
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/admin/users')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Kullanıcılar
              </button>
              <div className="ml-4 border-l border-gray-700 pl-4 flex items-center space-x-4">
                <div 
                  className="flex items-center space-x-3 bg-gray-800/50 rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-800/70 transition-colors"
                  onClick={() => navigate('/profile')}
                >
                  <img src={user?.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-gray-400">Admin</p>
                  </div>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                >
                  Çıkış
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">⏳ Onay Bekleyen Görevler</h2>
          <p className="text-gray-400">Admin yardımcıları tarafından oluşturulan görev önerileri</p>
        </div>

        {pendingTasks.length === 0 ? (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-12 text-center">
              <p className="text-gray-400">Onay bekleyen görev yok</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pendingTasks.map((task) => (
              <Card
                key={task.id}
                className="bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border-yellow-700/50"
              >
                <CardHeader>
                  <CardTitle className="text-white text-lg">{task.title}</CardTitle>
                  <p className="text-sm text-gray-400 mt-2">{task.description}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <Badge className={priorityColors[task.priority]}>
                      {priorityLabels[task.priority]}
                    </Badge>
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                      {departmentLabels[task.created_by_department]}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 text-xs text-gray-400">
                    <p><span className="text-gray-500">Öneren:</span> {task.created_by_name}</p>
                    <div className="pt-2 border-t border-gray-700">
                      <p className="text-gray-500 mb-1">Atanan Kişiler:</p>
                      {task.assigned_to && (
                        <p className="text-gray-300">
                          • {users.find(u => u.id === task.assigned_to)?.name || 'Bilinmeyen'}
                        </p>
                      )}
                      {task.assigned_to_2 && (
                        <p className="text-gray-300">
                          • {users.find(u => u.id === task.assigned_to_2)?.name || 'Bilinmeyen'}
                        </p>
                      )}
                      {task.assigned_to_3 && (
                        <p className="text-gray-300">
                          • {users.find(u => u.id === task.assigned_to_3)?.name || 'Bilinmeyen'}
                        </p>
                      )}
                    </div>
                    <p className="pt-2"><span className="text-gray-500">Tarih:</span> {new Date(task.created_at).toLocaleDateString('tr-TR')}</p>
                    <p><span className="text-gray-500">Başlangıç:</span> {new Date(task.start_date).toLocaleDateString('tr-TR')}</p>
                    <p><span className="text-gray-500">Son Tarih:</span> {new Date(task.due_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={() => handleApprove(task.id)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      ✓ Onayla
                    </Button>
                    <Button
                      onClick={() => handleReject(task.id)}
                      variant="outline"
                      className="flex-1 bg-red-900/20 border-red-700 text-red-400 hover:bg-red-900/40"
                    >
                      ✗ Reddet
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PendingTasksPage;
